import { CachedLearningAudio } from '@shared/media/audio/CachedLearningAudio'

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
} from 'react'
import LoopRoundedIcon from '@mui/icons-material/LoopRounded'
import PauseCircleFilledRoundedIcon from '@mui/icons-material/PauseCircleFilledRounded'
import PauseCircleOutlineRoundedIcon from '@mui/icons-material/PauseCircleOutlineRounded'
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded'
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import SkipNextRoundedIcon from '@mui/icons-material/SkipNextRounded'
import SkipPreviousRoundedIcon from '@mui/icons-material/SkipPreviousRounded'
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { useDashboardState } from '@features/dashboard/store/useDashboardState'
import {
  formatLessonTitle,
  resolveLessonDescription,
} from '@features/learning/shared/learning-helpers'
import type { LearningLesson } from '@services/firebase/learning'
import { sharedAssets } from '@shared/assets'
import { LegacyAudioSettingsPopover as LegacySharedAudioSettingsPopover } from '../../shared/LegacyAudioSettingsPopover'
import {
  LEGACY_AUDIO_LOOP_OPTIONS,
  LEGACY_AUDIO_SPEED_OPTIONS,
} from '../../shared/useLegacyAudioController'
import { LegacyAudioWaveIcon } from '../shared/LegacyAudioWaveIcon'
import { LegacyBookState } from '../shared/LegacyOnlineBookShell'
import { useStudyTimeTracker } from '@features/learning/shared/useStudyTimeTracker'

function playAudio(audio: HTMLAudioElement | null | undefined) {
  if (!audio) {
    return
  }

  const playResult = audio.play()

  if (playResult) {
    void playResult.catch(() => undefined)
  }
}

type LegacyAudiobookAudioSettingsMode = 'main' | 'speed' | 'loop' | null

function LegacyAudiobookAudioSettingsPopover({
  anchorEl,
  loopLevel,
  onClose,
  onLoopLevelChange,
  onPlaybackRateChange,
  playbackRate,
  settingsMode,
  setSettingsMode,
}: {
  readonly anchorEl: HTMLButtonElement | null
  readonly loopLevel: number
  readonly onClose: () => void
  readonly onLoopLevelChange: (value: number) => void
  readonly onPlaybackRateChange: (value: number) => void
  readonly playbackRate: number
  readonly settingsMode: LegacyAudiobookAudioSettingsMode
  readonly setSettingsMode: (value: LegacyAudiobookAudioSettingsMode) => void
}) {
  const playbackLabel = playbackRate === 1 ? 'Normal' : '0.75x'
  const isMainView = settingsMode === 'main'
  const isDetailView = settingsMode === 'speed' || settingsMode === 'loop'
  const detailOptions =
    settingsMode === 'speed' ? LEGACY_AUDIO_SPEED_OPTIONS : LEGACY_AUDIO_LOOP_OPTIONS
  const detailTitle = settingsMode === 'speed' ? 'Speed' : 'Loop'

  const renderMainRow = (
    label: 'Loop' | 'Speed',
    value: string,
    onClick: () => void,
  ) => (
    <button className="legacyAudioSettingForward" onClick={onClick} type="button">
      <span className="legacyAudioSettingButtonContent">
        <span>{label}</span>
        <span className="legacyAudioSettingButtonValue">{value}</span>
      </span>
    </button>
  )

  return (
    <LegacySharedAudioSettingsPopover
      anchorEl={anchorEl}
      onClose={onClose}
      paperClassName="legacyAudioSettingsPopover"
      transitionDuration={0}
    >
      {isMainView ? (
        <div className="legacyAudioSettingsList">
          {renderMainRow('Speed', playbackLabel, () => {
            setSettingsMode('speed')
          })}
          {renderMainRow('Loop', `${loopLevel}x`, () => {
            setSettingsMode('loop')
          })}
        </div>
      ) : isDetailView ? (
        <>
          <button
            className="legacyAudioSettingBack"
            onClick={() => {
              setSettingsMode('main')
            }}
            type="button"
          >
            <span className="legacyAudioSettingButtonContent">{detailTitle}</span>
          </button>
          <div className="legacyAudioSettingsList">
            {detailOptions.map((value) => {
              const isCurrent =
                settingsMode === 'speed' ? playbackRate === value : loopLevel === value

              return (
                <button
                  className={[
                    'legacyAudioSettingOption',
                    isCurrent ? 'current' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  key={value}
                  onClick={() => {
                    if (settingsMode === 'speed') {
                      onPlaybackRateChange(value)
                    } else {
                      onLoopLevelChange(value)
                    }
                    setSettingsMode('main')
                  }}
                  type="button"
                >
                  <span>{value === 1 ? 'Normal' : `${value}x`}</span>
                </button>
              )
            })}
          </div>
        </>
      ) : null}
    </LegacySharedAudioSettingsPopover>
  )
}

function getAudioLessonLabel(
  t: TFunction<'book'>,
  lesson: LearningLesson,
  locale: string,
  index: number,
) {
  return formatLessonTitle(t, lesson, locale, index + 1)
}

function LegacyAudioLessonRow({
  current,
  description,
  lesson,
  onSelect,
  playing,
  title,
}: {
  readonly current: boolean
  readonly description?: string
  readonly lesson: LearningLesson
  readonly onSelect: () => void
  readonly playing: boolean
  readonly title: string
}) {
  const hasAudio = Boolean(lesson.audio)

  return (
    <section className="legacyAudioLesson">
      <div className="legacyAudioLessonContent">
        <button
          className="legacyAudioLessonBody"
          disabled={!hasAudio}
          onClick={onSelect}
          type="button"
        >
          <span className={`legacyAudioLessonIcon ${current ? 'current' : ''}`}>
            <span className={`btnIcon ${playing && current ? 'audio-playing' : ''}`}>
              {hasAudio ? <LegacyAudioWaveIcon /> : <VolumeOffRoundedIcon />}
            </span>
          </span>
          <span className="legacyAudioLessonTexts">
            <span className="legacyAudioLessonTitle">{title}</span>
            {description ? (
              <span className="legacyAudioLessonDescription">{description}</span>
            ) : null}
          </span>
        </button>
      </div>
    </section>
  )
}

export function LegacyOnlineAudiobooksScreen() {
  const { t, i18n } = useTranslation('book')
  const { actions, state } = useDashboardState()
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const shouldAutoplayRef = useRef(false)
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [position, setPosition] = useState(0)
  const [duration, setDuration] = useState(0)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [loopLevel, setLoopLevel] = useState(3)
  const [loopAction, setLoopAction] = useState(3)
  const [isLoopActive, setIsLoopActive] = useState(false)
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLButtonElement | null>(null)
  const [settingsMode, setSettingsMode] =
    useState<LegacyAudiobookAudioSettingsMode>(null)
  const selectedIndex = useMemo(() => {
    const requestedLessonId = selectedLessonId ?? state.lesson?.id
    const requestedIndex = requestedLessonId
      ? state.lessons.findIndex((lesson) => lesson.id === requestedLessonId)
      : -1
    const firstAudioIndex = state.lessons.findIndex((lesson) => Boolean(lesson.audio))

    if (requestedIndex >= 0) {
      return requestedIndex
    }

    return Math.max(firstAudioIndex, 0)
  }, [selectedLessonId, state.lesson?.id, state.lessons])
  const selectedLesson = state.lessons[selectedIndex] ?? null
  useStudyTimeTracker({
    enabled: Boolean(selectedLesson?.audio),
    isPlaying,
    surface: 'audio-book',
  })
  const previousPlayableIndex = useMemo(() => {
    for (let index = selectedIndex - 1; index >= 0; index -= 1) {
      if (state.lessons[index]?.audio) {
        return index
      }
    }

    return -1
  }, [selectedIndex, state.lessons])
  const nextPlayableIndex = useMemo(() => {
    for (let index = selectedIndex + 1; index < state.lessons.length; index += 1) {
      if (state.lessons[index]?.audio) {
        return index
      }
    }

    return -1
  }, [selectedIndex, state.lessons])
  const loopProgress = loopLevel > 0 ? (loopAction / loopLevel) * 100 : 0
  const loopDashOffset = 314.159 - (314.159 / 100) * loopProgress

  useEffect(() => {
    const audio = audioRef.current

    if (!audio) {
      return
    }

    audio.playbackRate = playbackRate
  }, [playbackRate, selectedLesson?.id])

  const handleSelectIndex = (index: number, autoplay = true) => {
    const lesson = state.lessons[index]

    if (!lesson?.audio) {
      return
    }

    shouldAutoplayRef.current = autoplay
    setSelectedLessonId(lesson.id)
    actions.setLesson(lesson)
    setIsPlaying(false)
    setPosition(0)
    setDuration(0)
    setLoopAction(loopLevel)
    setSettingsAnchor(null)
    setSettingsMode(null)
  }

  const handleSelectLessonRow = (index: number) => {
    if (selectedIndex === index) {
      handleTogglePlay()
      return
    }

    handleSelectIndex(index)
  }

  const handleTogglePlay = () => {
    const audio = audioRef.current

    if (!audio || !selectedLesson?.audio) {
      return
    }

    if (isPlaying) {
      audio.pause()
      return
    }

    playAudio(audio)
  }

  const handleEnded = () => {
    setPosition(0)

    if (isLoopActive && loopAction > 0) {
      setLoopAction((value) => value - 1)
      window.setTimeout(() => {
        playAudio(audioRef.current)
      }, 500)
      return
    }

    setLoopAction(loopLevel)
    setIsLoopActive(false)

    if (nextPlayableIndex >= 0) {
      handleSelectIndex(nextPlayableIndex)
    }
  }

  const handleProgressChange = (value: number) => {
    const audio = audioRef.current

    if (!audio || !duration) {
      return
    }

    audio.currentTime = (value / 100) * duration
    setPosition(value)
  }

  const handleLoop = () => {
    if (isLoopActive) {
      setIsLoopActive(false)
      setLoopAction(loopLevel)
      return
    }

    setIsLoopActive(true)
    setLoopAction(loopLevel)
    playAudio(audioRef.current)
  }

  const handleSetting = (event: MouseEvent<HTMLButtonElement>) => {
    if (settingsAnchor) {
      setSettingsAnchor(null)
      setSettingsMode(null)
      return
    }

    setSettingsAnchor(event.currentTarget)
    setSettingsMode('main')
  }

  const handleCloseAudioSettings = () => {
    setSettingsAnchor(null)
    setSettingsMode(null)
  }

  const handleDismissAudioSettingsLayer = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    handleCloseAudioSettings()
  }

  const handleChangeSpeed = (speed: number) => {
    setPlaybackRate(speed)

    if (audioRef.current) {
      audioRef.current.playbackRate = speed
    }
  }

  const handleChangeLoop = (value: number) => {
    setLoopLevel(value)
    setLoopAction(value)
  }

  return (
    <>
      {settingsAnchor ? (
        <div
          aria-hidden="true"
          className="legacyAudioSettingsDismissLayer"
          onClick={handleDismissAudioSettingsLayer}
          onMouseDown={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}
        />
      ) : null}
      <div className="legacyOnlineBookContainer legacyAudioContainer">
        {state.isLessonsLoading ? (
          <LegacyBookState message="Loading audiobook lessons..." />
        ) : null}
        {!state.isLessonsLoading && state.lessonsErrorMessage ? (
          <LegacyBookState message={state.lessonsErrorMessage} />
        ) : null}
        {!state.isLessonsLoading &&
        !state.lessonsErrorMessage &&
        state.lessons.length === 0 ? (
          <LegacyBookState message="No audiobook lessons were found for this program." />
        ) : null}
        {!state.isLessonsLoading && !state.lessonsErrorMessage && selectedLesson ? (
          <div className="legacyAudioWrap">
            <div className="legacyAudioLeftWrap">
              <div className="legacyAudioPlayerPanel">
                <div className="legacyAudioBook">
                  <button
                    aria-label={isPlaying ? 'Pause audiobook' : 'Play audiobook'}
                    className="legacyAudioCoverButton"
                    onClick={handleTogglePlay}
                    type="button"
                  >
                    <span
                      className={`legacyAudioCoverBook ${isPlaying ? 'playing' : ''}`}
                    >
                      <img
                        alt=""
                        onError={(event) => {
                          event.currentTarget.src = sharedAssets.image('avatar.svg')
                        }}
                        src={state.book?.featuredImage ?? sharedAssets.image('avatar.svg')}
                      />
                    </span>
                    <span className="legacyAudioCoverOverlay">
                      {isPlaying ? (
                        <PauseCircleOutlineRoundedIcon />
                      ) : (
                        <PlayCircleOutlineRoundedIcon />
                      )}
                    </span>
                  </button>
                </div>
                <div className="legacyAudioTitle">
                  <div className="dialogeTitle">
                    <div>
                      {getAudioLessonLabel(
                        t,
                        selectedLesson,
                        i18n.language,
                        selectedIndex,
                      )}
                    </div>
                    <div>
                      {getAudioLessonLabel(
                        t,
                        selectedLesson,
                        i18n.language,
                        selectedIndex,
                      )}
                    </div>
                  </div>
                </div>
                <input
                  aria-label="Audio progress"
                  className="legacyAudioProgressSlider"
                  max={100}
                  min={0}
                  onChange={(event) => {
                    handleProgressChange(Number(event.target.value))
                  }}
                  style={
                    {
                      '--legacy-audio-progress': `${position}%`,
                    } as CSSProperties
                  }
                  type="range"
                  value={position}
                />
                <div className="legacyAudioActions">
                  <button
                    disabled={previousPlayableIndex < 0}
                    onClick={() => {
                      handleSelectIndex(previousPlayableIndex)
                    }}
                    type="button"
                  >
                    <SkipPreviousRoundedIcon />
                  </button>
                  <button
                    className={`legacyAudioPlayButton ${isPlaying ? 'playing' : ''}`}
                    onClick={handleTogglePlay}
                    type="button"
                  >
                    {isPlaying ? (
                      <PauseCircleFilledRoundedIcon />
                    ) : (
                      <PlayCircleFilledRoundedIcon />
                    )}
                  </button>
                  <button
                    disabled={nextPlayableIndex < 0}
                    onClick={() => {
                      handleSelectIndex(nextPlayableIndex)
                    }}
                    type="button"
                  >
                    <SkipNextRoundedIcon />
                  </button>
                </div>
                <div className="legacyAudioSettings">
                  <button
                    aria-label={isLoopActive ? 'Disable audio loop' : 'Enable audio loop'}
                    className={[
                      'legacyAudioSettingsButton',
                      'legacyAudioLoopButton',
                      isLoopActive ? 'active' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={handleLoop}
                    type="button"
                  >
                    {isLoopActive ? (
                      <span
                        className="legacyAudioLoopProgress"
                        style={
                          {
                            '--legacy-loop-offset': loopDashOffset,
                          } as CSSProperties
                        }
                      >
                        <svg aria-hidden="true" viewBox="0 0 120 120">
                          <circle
                            className="legacyAudioLoopProgressBase"
                            cx="60"
                            cy="60"
                            r="50"
                          />
                          <circle
                            className="legacyAudioLoopProgressActive"
                            cx="60"
                            cy="60"
                            r="50"
                          />
                        </svg>
                      </span>
                    ) : (
                      <LoopRoundedIcon />
                    )}
                    {isLoopActive ? (
                      <span className="legacyAudioLoopCount">{loopAction}</span>
                    ) : null}
                  </button>
                  <button
                    aria-label="Audio settings"
                    className={[
                      'legacyAudioSettingsButton',
                      'legacyAudioSettingsToggle',
                      settingsAnchor ? 'active' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={handleSetting}
                    type="button"
                  >
                    <SettingsRoundedIcon />
                  </button>
                </div>
                {settingsAnchor ? (
                  <LegacyAudiobookAudioSettingsPopover
                    anchorEl={settingsAnchor}
                    loopLevel={loopLevel}
                    onClose={handleCloseAudioSettings}
                    onLoopLevelChange={handleChangeLoop}
                    onPlaybackRateChange={handleChangeSpeed}
                    playbackRate={playbackRate}
                    setSettingsMode={setSettingsMode}
                    settingsMode={settingsMode}
                  />
                ) : null}
              </div>
            </div>
            <div className="legacyAudioRightWrap">
              {state.lessons.map((lesson, index) => (
                <LegacyAudioLessonRow
                  current={selectedIndex === index}
                  description={resolveLessonDescription(lesson, i18n.language)}
                  key={lesson.id}
                  lesson={lesson}
                  onSelect={() => {
                    handleSelectLessonRow(index)
                  }}
                  playing={isPlaying}
                  title={getAudioLessonLabel(t, lesson, i18n.language, index)}
                />
              ))}
            </div>
            {selectedLesson.audio ? (
              <CachedLearningAudio
                key={selectedLesson.id}
                onEnded={handleEnded}
                onLoadedData={(event) => {
                  setDuration(event.currentTarget.duration || 0)

                  if (!shouldAutoplayRef.current) {
                    return
                  }

                  shouldAutoplayRef.current = false
                  event.currentTarget.currentTime = 0
                  playAudio(event.currentTarget)
                }}
                onLoadStart={(event) => {
                  const audio = event.currentTarget
                  audio.playbackRate = playbackRate
                  audio.currentTime = 0
                  setPosition(0)
                  setDuration(0)
                }}
                onPause={() => {
                  setIsPlaying(false)
                }}
                onPlay={() => {
                  setIsPlaying(true)
                }}
                onTimeUpdate={() => {
                  const audio = audioRef.current

                  if (!audio || !audio.duration) {
                    return
                  }

                  setPosition((audio.currentTime / audio.duration) * 100)
                }}
                ref={audioRef}
                src={selectedLesson.audio}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  )
}

